import React, { useState, useEffect } from 'react';
import { RouterProvider, createBrowserRouter, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { DevThemeProvider } from './config/DevThemeConfig';
import DevThemePanel from './components/admin/DevThemePanel';
import { ABTestProvider } from './contexts/ABTestContext';
import { CartProvider } from './contexts/CartContext';
import { useAuth } from './contexts/AuthContext';
import routes from './routes';
import { ConsentProvider } from './contexts/ConsentContext';
import { ApiStatusProvider } from './contexts/ApiStatusContext';
import ApiStatusGate from './components/ApiStatusGate';
import ConsentBanner from './components/consent/ConsentBanner';
import ConsentPreferencesPanel from './components/consent/ConsentPreferencesPanel';
import AuthModal from './components/auth/AuthModal';
import NotificationToastContainer from './components/notifications/NotificationToastContainer';
import errorLoggingService, { trackNavigation } from './services/errorLoggingService';
import { setupTokenRefreshInterceptor, clearTokens, redirectToLogin } from './services/tokenRefreshInterceptor';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import SessionTimeoutModal from './components/session/SessionTimeoutModal';

// Initialise the token refresh interceptor once at app startup
setupTokenRefreshInterceptor();

/**
 * Note: User ID for A/B testing is now obtained from AuthContext.
 * Anonymous users are tracked via sessionId only.
 * The backend manages user IDs - frontend does not generate them.
 */

/**
 * Generate or retrieve a session ID
 * Security: Uses crypto.randomUUID() for cryptographically secure randomness
 */
function getOrCreateSessionId() {
  const STORAGE_KEY = 'outdoorguides_session_id';

  try {
    // Generate a new session ID each time the app loads using secure random UUID
    const sessionId = crypto.randomUUID();
    sessionStorage.setItem(STORAGE_KEY, sessionId);
    return sessionId;
  } catch (error) {
    return null;
  }
}

/**
 * Navigation Tracker Component
 * Tracks route changes for error logging context
 */
function _NavigationTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track navigation on route change
    trackNavigation(location.pathname + location.search);
  }, [location]);

  return null;
}

/**
 * Hydration Fallback Component
 * Displayed during initial app hydration (React Router v7)
 */
function HydrateFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading OutdoorGuides...</p>
      </div>
    </div>
  );
}

/**
 * Router instance with SSR-ready loaders
 * Created outside of App component for optimal performance
 *
 * DEPLOYMENT NOTE:
 * ────────────────────────────────────────────────────────────────
 * This router configuration is SSR-ready. When deploying to production:
 *
 * 1. For Remix (Recommended):
 *    - Install: npm install @remix-run/node @remix-run/react
 *    - Convert routes.jsx to Remix route modules
 *    - Deploy to Vercel/Fly.io/Railway
 *
 * 2. For Custom Express SSR:
 *    - Use createStaticHandler from react-router-dom/server
 *    - Add SSR middleware to apps/api/src/index.js
 *    - Render to string on server, hydrate on client
 *
 * 3. For Static Site Generation:
 *    - Pre-render all known routes at build time
 *    - Deploy static files to S3/CloudFront
 */
// Wrap routes in a pathless layout that provides HydrateFallback for all pages (SCRUM-965)
const router = createBrowserRouter([{ hydrateFallbackElement: <HydrateFallback />, children: routes }]);

/**
 * Main Application Component
 * OutdoorGuides Platform - Epic 1.1.1: User Registration & Authentication
 * + MVP 1.10: A/B Testing & Experimentation Framework
 *
 * Provider Hierarchy:
 * 1. GoogleOAuthProvider - Google authentication
 * 2. AuthProvider (from main.jsx) - User authentication state
 * 3. ConsentProvider - GDPR cookie consent state (SCRUM-1446)
 * 4. ABTestProvider - A/B testing experiments
 * 5. DevThemeProvider - Development theme system
 * 6. RouterProvider - React Router v7 with loaders (SSR-ready)
 */
export default function App() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signin');

  // Get authenticated user from AuthContext
  const { user } = useAuth();

  // Session timeout warning (SCRUM-366, MVP 1.15 V2 Epic 4)
  const { showWarning, userCategory } = useSessionTimeout();

  function handleSessionExpired() {
    clearTokens();
    redirectToLogin();
    window.location.replace('/login');
  }

  // For A/B testing: use real userId if authenticated, otherwise null
  // Anonymous users are tracked via sessionId only
  const userId = user?.id || null;
  const sessionId = getOrCreateSessionId();

  // Initialize error logging service on mount
  useEffect(() => {
    errorLoggingService.initialize();

    // Cleanup on unmount
    return () => {
      errorLoggingService.cleanup();
    };
  }, []);

  const _handleAuthClick = () => {
    setShowAuthModal(true);
    setAuthMode('signin');
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
  };

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
      <ApiStatusProvider>
      <ConsentProvider>
        <CartProvider>
        <ABTestProvider userId={userId} sessionId={sessionId}>
          <DevThemeProvider>
            {/* Auth modal - available across all routes */}
            <AuthModal
              showModal={showAuthModal}
              onClose={handleCloseAuthModal}
              authMode={authMode}
              setAuthMode={setAuthMode}
            />

            {/* Session timeout warning — internal staff only (SCRUM-366) */}
            {!!user && (
              <SessionTimeoutModal
                isOpen={showWarning}
                userCategory={userCategory}
                onExtend={() => {/* guides/customers: interceptor handles refresh */}}
                onExpired={handleSessionExpired}
              />
            )}

            {/* API unavailability redirect gate (SCRUM-1983) */}
            <ApiStatusGate />

            {/* Real-time notification toasts (MVP 1.21.9) */}
            <NotificationToastContainer />

            {/* React Router v7 with data loaders (SSR-ready) */}
            <RouterProvider router={router} />

            {/* Development Theme Panel - Press Ctrl+Shift+A to toggle */}
            {import.meta.env.DEV && <DevThemePanel />}

            {/* GDPR Cookie Consent (SCRUM-1446) */}
            <ConsentBanner />
            <ConsentPreferencesPanel />
          </DevThemeProvider>
        </ABTestProvider>
      </CartProvider>
      </ConsentProvider>
      </ApiStatusProvider>
    </GoogleOAuthProvider>
  );
}
